import { Check } from 'lucide-react'
import { useMemo } from 'react'
import { canonicalStatus, type UserProgressStatus } from '@/lib/progress-status'
import { cn } from '@/lib/utils'

interface DifficultyStats {
  total: number
  solved: number
}

interface ProgressSummaryProps {
  problems: { id: string; difficulty: { name: string } | null }[]
  progress: Record<string, UserProgressStatus> | undefined
  className?: string
}

const DIFFICULTY_CONFIG = {
  Easy: { color: '#00b8a3', dimColor: '#00b8a333', label: 'Easy' },
  Medium: { color: '#ffc01e', dimColor: '#ffc01e33', label: 'Med.' },
  Hard: { color: '#ff375f', dimColor: '#ff375f33', label: 'Hard' },
} as const

type DifficultyName = keyof typeof DIFFICULTY_CONFIG

export function ProgressSummary({
  problems,
  progress,
  className,
}: ProgressSummaryProps) {
  const stats = useMemo(() => {
    const byDifficulty: Record<DifficultyName, DifficultyStats> = {
      Easy: { total: 0, solved: 0 },
      Medium: { total: 0, solved: 0 },
      Hard: { total: 0, solved: 0 },
    }

    let totalSolved = 0
    let totalAttempting = 0

    for (const problem of problems) {
      const name = problem.difficulty?.name as DifficultyName | undefined
      if (name && name in byDifficulty) {
        byDifficulty[name].total++
      }

      const status = canonicalStatus(progress?.[problem.id])
      if (status === 'solved') {
        totalSolved++
        if (name && name in byDifficulty) {
          byDifficulty[name].solved++
        }
      } else if (status === 'attempted') {
        totalAttempting++
      }
    }

    return {
      byDifficulty,
      totalSolved,
      totalAttempting,
      totalProblems: problems.length,
    }
  }, [problems, progress])

  // SVG donut chart — partial arc (270° out of 360°)
  const radius = 78
  const strokeWidth = 11
  const circumference = 2 * Math.PI * radius
  const center = 93
  const arcDegrees = 270
  const arcLength = (arcDegrees / 360) * circumference
  // Rotate so the arc starts at bottom-left (7:30 position)
  // SVG circle starts at 3 o'clock. We want to start at 225° (bottom-left).
  // 225° from 12 o'clock = 135° from 3 o'clock (SVG default).
  const startRotation = 135

  // Two-layer donut: dim arcs for total per difficulty, bright arcs for solved
  const { bgArcs, fgArcs } = useMemo(() => {
    const total = stats.totalProblems || 1
    const gap = 4 // gap between difficulty segments
    const diffs: DifficultyName[] = ['Easy', 'Medium', 'Hard']

    const bg: {
      name: string
      dashArray: string
      dashOffset: number
      color: string
    }[] = []
    const fg: {
      name: string
      dashArray: string
      dashOffset: number
      color: string
    }[] = []
    let offset = 0

    for (const name of diffs) {
      const config = DIFFICULTY_CONFIG[name]
      const diffTotal = stats.byDifficulty[name].total
      const diffSolved = stats.byDifficulty[name].solved

      if (diffTotal <= 0) continue

      const totalFraction = diffTotal / total
      const segLength = totalFraction * arcLength
      const effectiveGap = offset > 0 ? gap : 0

      // Dim background arc (full difficulty segment)
      bg.push({
        name,
        dashArray: `${Math.max(0, segLength - effectiveGap)} ${circumference - segLength + effectiveGap}`,
        dashOffset: -(offset + effectiveGap),
        color: config.dimColor,
      })

      // Bright foreground arc (solved portion only, same start position)
      if (diffSolved > 0) {
        const solvedFraction = diffSolved / total
        const solvedLength = solvedFraction * arcLength
        fg.push({
          name,
          dashArray: `${Math.max(0, solvedLength - effectiveGap)} ${circumference - solvedLength + effectiveGap}`,
          dashOffset: -(offset + effectiveGap),
          color: config.color,
        })
      }

      offset += segLength
    }

    return { bgArcs: bg, fgArcs: fg }
  }, [stats, arcLength, circumference])

  const difficulties = ['Easy', 'Medium', 'Hard'] as const

  return (
    <div className={cn('flex items-center', className)}>
      {/* Donut chart — centered, scales down on small screens */}
      <div className="relative mx-auto mt-3 shrink-0 max-sm:scale-75 max-sm:origin-center max-sm:-my-4">
        <svg
          width={center * 2}
          height={center * 2}
          viewBox={`0 0 ${center * 2} ${center * 2}`}
        >
          {/* Dim background arcs (total per difficulty) */}
          {bgArcs.map((arc) => (
            <circle
              key={`bg-${arc.name}`}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={arc.color}
              strokeWidth={strokeWidth}
              strokeDasharray={arc.dashArray}
              strokeDashoffset={arc.dashOffset}
              transform={`rotate(${startRotation} ${center} ${center})`}
            />
          ))}
          {/* Bright foreground arcs (solved per difficulty) */}
          {fgArcs.map((arc) => (
            <circle
              key={`fg-${arc.name}`}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={arc.color}
              strokeWidth={strokeWidth}
              strokeDasharray={arc.dashArray}
              strokeDashoffset={arc.dashOffset}
              transform={`rotate(${startRotation} ${center} ${center})`}
            />
          ))}
        </svg>
        {/* Center text — shifted slightly above center */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center"
          style={{ marginTop: -8 }}
        >
          <div className="text-foreground leading-tight">
            <span className="text-3xl font-bold">{stats.totalSolved}</span>
            <span className="text-muted-foreground text-sm">
              /{stats.totalProblems}
            </span>
          </div>
          <div className="text-foreground flex items-center gap-1 text-sm">
            <Check className="h-3.5 w-3.5 text-green-500" />
            Solved
          </div>
        </div>
        {/* Attempting label — positioned in the arc gap at the bottom */}
        <div className="absolute inset-x-0 bottom-5 text-center text-xs">
          <span className="text-muted-foreground">
            <span className="text-yellow-500">{stats.totalAttempting}</span>{' '}
            Attempting
          </span>
        </div>
      </div>

      {/* Difficulty breakdown — pinned right */}
      <div className="flex w-24 shrink-0 flex-col gap-2">
        {difficulties.map((name) => {
          const config = DIFFICULTY_CONFIG[name]
          const diffStats = stats.byDifficulty[name]

          return (
            <div
              key={name}
              className="bg-muted/50 rounded-lg px-3 py-2 text-center"
            >
              <div
                className="text-xs font-medium"
                style={{ color: config.color }}
              >
                {config.label}
              </div>
              <div className="text-foreground text-sm font-semibold">
                {diffStats.solved}
                <span className="text-muted-foreground font-normal">
                  /{diffStats.total}
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
